import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import SaveIcon from "@material-ui/icons/Save";
import StorageIcon from "@material-ui/icons/Storage";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(3),
    borderRadius: 24,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(20, 83, 77, 0.34), rgba(15, 23, 42, 0.92))"
        : "linear-gradient(135deg, #eefdf8 0%, #f8fbff 100%)",
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(94, 234, 212, 0.16)" : "#d6eee9"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 18px 45px rgba(0,0,0,0.32)"
        : "0 18px 45px rgba(15, 23, 42, 0.08)"
  },
  card: {
    padding: theme.spacing(2.5),
    borderRadius: 18,
    height: "100%"
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.75)
  },
  helper: {
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  gridGap: {
    rowGap: theme.spacing(2)
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center"
  },
  statusChip: {
    fontWeight: 700
  },
  mono: {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
    fontSize: 12
  }
}));

const defaultSettings = {
  enabled: false,
  provider: "local",
  scheduleTime: "03:00",
  retentionDays: 7,
  bucket: "",
  region: "us-east-1",
  endpoint: "",
  accessKeyId: "",
  secretAccessKey: "",
  forcePathStyle: false,
  prefix: "altchat-backups",
  includeMedia: true,
  includeSessions: true,
  includeEnv: true,
  keepLocalAfterUpload: true
};

const formatBytes = value => {
  const size = Number(value || 0);
  if (!size) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(units.length - 1, Math.floor(Math.log(size) / Math.log(1024)));
  const normalized = size / Math.pow(1024, index);
  return `${normalized.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatDateTime = value => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR");
};

const statusMeta = {
  success: { label: "Sucesso", color: "primary" },
  failed: { label: "Falhou", color: "secondary" },
  running: { label: "Executando", color: "default" },
  queued: { label: "Na fila", color: "default" }
};

const Backups = () => {
  const classes = useStyles();
  const [settings, setSettings] = useState(defaultSettings);
  const [jobs, setJobs] = useState([]);
  const [database, setDatabase] = useState({});
  const [localBackupDir, setLocalBackupDir] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);

  const latestSuccess = useMemo(
    () => jobs.find(job => job.status === "success") || null,
    [jobs]
  );
  const runningJob = useMemo(
    () => jobs.find(job => job.status === "running") || null,
    [jobs]
  );

  const loadBackups = useCallback(async () => {
    try {
      const { data } = await api.get("/backups");
      setSettings(current => ({
        ...current,
        ...(data.settings || {})
      }));
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
      setDatabase(data.database || {});
      setLocalBackupDir(data.localBackupDir || "");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBackups();
  }, [loadBackups]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (runningJob) {
        loadBackups();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [loadBackups, runningJob]);

  const handleChange = event => {
    const { name, value, checked, type } = event.target;
    setSettings(current => ({
      ...current,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/backups/settings", settings);
      setSettings(current => ({ ...current, ...data }));
      toast.success("Configuração de backup salva.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleTestStorage = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/backups/test-storage", settings);
      toast.success(`Destino validado no bucket ${data.bucket}.`);
    } catch (err) {
      toastError(err);
    } finally {
      setTesting(false);
    }
  };

  const handleRunBackup = async () => {
    setRunning(true);
    try {
      await api.post("/backups/run");
      toast.success("Backup iniciado. Acompanhe o status no histórico.");
      await loadBackups();
    } catch (err) {
      toastError(err);
    } finally {
      setRunning(false);
    }
  };

  return (
    <MainContainer scrollY="visible" fullHeight={false}>
      <MainHeader>
        <Title>Backups</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            onClick={loadBackups}
            disabled={loading}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={handleRunBackup}
            disabled={running || Boolean(runningJob)}
          >
            Executar agora
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.hero} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="h5" style={{ fontWeight: 800 }}>
              Backup automático do AltChat
            </Typography>
            <Typography className={classes.helper}>
              Configure pela tela do superadmin. O backend gera o dump do Postgres,
              empacota arquivos criticos e salva localmente ou em S3 compativel.
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box className={classes.actions} justifyContent="flex-end">
              <Chip
                className={classes.statusChip}
                label={settings.enabled ? "Automático ativo" : "Automático desligado"}
                color={settings.enabled ? "primary" : "default"}
              />
              <Chip
                className={classes.statusChip}
                label={runningJob ? "Backup em andamento" : "Pronto"}
                color={runningJob ? "secondary" : "default"}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper className={classes.card} variant="outlined">
            <Typography className={classes.sectionTitle}>Configuração</Typography>
            <Grid container spacing={2} className={classes.gridGap}>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(settings.enabled)}
                      onChange={handleChange}
                      name="enabled"
                    />
                  }
                  label="Ativado"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="time"
                  label="Horario diario"
                  name="scheduleTime"
                  value={settings.scheduleTime}
                  onChange={handleChange}
                  inputProps={{ step: 300 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Retenção local (dias)"
                  name="retentionDays"
                  value={settings.retentionDays}
                  onChange={handleChange}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Destino</InputLabel>
                  <Select
                    name="provider"
                    value={settings.provider}
                    onChange={handleChange}
                    label="Destino"
                  >
                    <MenuItem value="local">Disco local</MenuItem>
                    <MenuItem value="s3">S3 compativel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Banco detectado"
                  value={`${database.name || "-"} @ ${database.host || "-"}:${database.port || "-"}`}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Caminho local"
                  value={localBackupDir || "-"}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Prefixo/caminho no storage"
                  name="prefix"
                  value={settings.prefix}
                  onChange={handleChange}
                  helperText="Ex.: altchat/postgres"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Box display="flex" flexWrap="wrap" gridGap={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={Boolean(settings.includeMedia)}
                        onChange={handleChange}
                        name="includeMedia"
                      />
                    }
                    label="Incluir midias"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={Boolean(settings.includeSessions)}
                        onChange={handleChange}
                        name="includeSessions"
                      />
                    }
                    label="Incluir sessões WhatsApp"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={Boolean(settings.includeEnv)}
                        onChange={handleChange}
                        name="includeEnv"
                      />
                    }
                    label="Incluir snapshot do ambiente"
                  />
                </Box>
              </Grid>

              {settings.provider === "s3" && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Bucket"
                      name="bucket"
                      value={settings.bucket}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Regiao"
                      name="region"
                      value={settings.region}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Endpoint"
                      name="endpoint"
                      value={settings.endpoint}
                      onChange={handleChange}
                      helperText="Ex.: R2, Wasabi, Backblaze"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Access Key"
                      name="accessKeyId"
                      value={settings.accessKeyId}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="password"
                      label="Secret Key"
                      name="secretAccessKey"
                      value={settings.secretAccessKey}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" flexDirection="column">
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={Boolean(settings.forcePathStyle)}
                            onChange={handleChange}
                            name="forcePathStyle"
                          />
                        }
                        label="Force path style"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={Boolean(settings.keepLocalAfterUpload)}
                            onChange={handleChange}
                            name="keepLocalAfterUpload"
                          />
                        }
                        label="Manter copia local"
                      />
                    </Box>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Box className={classes.actions}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    Salvar configuração
                  </Button>
                  {settings.provider === "s3" && (
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<StorageIcon />}
                      onClick={handleTestStorage}
                      disabled={testing}
                    >
                      Testar destino
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.card} variant="outlined">
            <Typography className={classes.sectionTitle}>Ultimo backup válido</Typography>
            {latestSuccess ? (
              <>
                <Typography variant="h6" style={{ fontWeight: 800 }}>
                  {formatDateTime(latestSuccess.finishedAt)}
                </Typography>
                <Typography className={classes.helper}>
                  Arquivo: {latestSuccess.fileName || "-"}
                </Typography>
                <Typography className={classes.helper}>
                  Tamanho: {formatBytes(latestSuccess.sizeBytes)}
                </Typography>
                <Typography className={`${classes.helper} ${classes.mono}`}>
                  SHA256: {latestSuccess.checksum || "-"}
                </Typography>
              </>
            ) : (
              <Typography className={classes.helper}>
                Nenhum backup concluido com sucesso ainda.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper className={classes.card} variant="outlined">
        <Typography className={classes.sectionTitle}>Histórico</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gatilho</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell>Arquivo</TableCell>
                <TableCell>Tamanho</TableCell>
                <TableCell>Início</TableCell>
                <TableCell>Fim</TableCell>
                <TableCell>Erro</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>Nenhum backup executado ainda.</TableCell>
                </TableRow>
              ) : (
                jobs.map(job => {
                  const meta = statusMeta[job.status] || {
                    label: job.status,
                    color: "default"
                  };
                  return (
                    <TableRow key={job.id}>
                      <TableCell>#{job.id}</TableCell>
                      <TableCell>
                        <Chip size="small" color={meta.color} label={meta.label} />
                      </TableCell>
                      <TableCell>{job.trigger === "scheduled" ? "Automático" : "Manual"}</TableCell>
                      <TableCell>{job.provider === "s3" ? job.bucket || "S3" : "Local"}</TableCell>
                      <TableCell>{job.fileName || "-"}</TableCell>
                      <TableCell>{formatBytes(job.sizeBytes)}</TableCell>
                      <TableCell>{formatDateTime(job.startedAt)}</TableCell>
                      <TableCell>{formatDateTime(job.finishedAt)}</TableCell>
                      <TableCell>{job.errorMessage || "-"}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </MainContainer>
  );
};

export default Backups;
